import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import type { FormControlState } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FocusService } from 'app/core/services/focus.service';
import { CustomValidators } from 'app/shared/validators/custom-validators';
import { ComboModel } from './combo-model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-combo',
  styleUrls: ['./combo.component.scss'],
  templateUrl: './combo.component.html',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatOptionModule,
    MatDividerModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
  ],
})
export class ComboComponent implements OnChanges {
  @ViewChild('_matSelect') public _matSelect: MatSelect;

  // The form control associated with the mat-select
  // For filters in list: the default value is used
  // For create and edit form: the input should be set by the parent component
  @Input() public comboFormControl = new FormControl<ComboModel | null | undefined>(null!);

  @Input() public defaultValue: ComboModel | null;

  // Use this input to disable the component only when a 'comboFormControl' is NOT provided by the parent component.
  // If a 'comboFormControl' is provide by the parent component, disable/enable the FormControl itself instead via it's disable()/enable() methods.
  @Input() public disabled: boolean | undefined = undefined;

  // Indicates whether the combo choice will display information about his existing parent inside combo.
  @Input() public displayParentLabelFlag = true;

  @Input() public eraseButtonFlag = false;

  @Input() public hint: string | null;

  // The selected uuid
  @Input() public keySelected: string | boolean | null | undefined;

  // Add specific css class when context is a list
  @Input() public listModeFlag = false;

  @Input() public matLabel: string;

  @Input() public nullableOption: string | null;

  // Available options
  @Input() public options: ComboModel[] | null | undefined;

  // The message to display if the required validator fails.
  @Input() public requiredFieldValidatorMessage: string;

  // Whether the field is required. if true a star will be displayed next to the field label.
  @Input() public requiredFlag: boolean | null | undefined;

  // Emitted every time a user change the selected value
  @Output() public selectedChange = new EventEmitter<ComboModel>();
  @Output() public selectionChange = new EventEmitter<MatSelectChange>();

  private _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private _focusService: FocusService = inject(FocusService);

  public static createForm(
    comboModel: ComboModel | null | undefined,
    requiredFlag: boolean,
    disabledFlag = false,
  ): FormControl<ComboModel | null> {
    const validators: ValidatorFn[] = [];
    if (requiredFlag) {
      validators.push(CustomValidators.required);
    }

    if (!comboModel?.key) {
      return new FormControl<ComboModel | null>(
        { value: undefined, disabled: disabledFlag } as unknown as FormControlState<ComboModel | null>,
        validators,
      );
    } else {
      return new FormControl<ComboModel | null>({ value: comboModel, disabled: disabledFlag }, validators);
    }
  }

  public compareComboModels(option1: ComboModel | null | undefined, option2: ComboModel | undefined): boolean {
    if (!option1 && !option2) return true;
    if (!option1 || !option2) return false;
    return option1.key === option2.key;
  }

  public eraseFilterClickedHandler(): void {
    if (this.defaultValue && this.defaultValue.key === this.comboFormControl.value?.key) {
      return;
    }
    this.comboFormControl.setValue(this.defaultValue!);
    this.valueChange(this.defaultValue);
  }

  public markForCheck(): void {
    this._changeDetectorRef.markForCheck();
  }

  public ngOnChanges(): void {
    if (this.options) {
      const selected = this.options.find((x) => x.key === this.keySelected);
      const currentValue = this.comboFormControl.value;
      if (!this.compareComboModels(currentValue, selected)) {
        this.comboFormControl.setValue(selected!);
        this._changeDetectorRef.markForCheck();
      }
    }

    this._setDisableOrEnable();
  }

  public onElementSelectionChange(matSelectChange: MatSelectChange): void {
    this.selectionChange.emit(matSelectChange);
    this._changeDetectorRef.markForCheck();
  }

  public resetCombo(): void {
    this.comboFormControl.setValue(undefined!);
  }

  public setFocus(): void {
    this._focusService.setTimeoutOutsideAngular(() => {
      this._matSelect?.focus();
    });
  }

  public valueChange(comboModel: ComboModel | null): void {
    this.selectedChange.emit(comboModel!);
  }

  private _disable(): void {
    this.comboFormControl.disable();
  }

  private _enable(): void {
    this.comboFormControl.enable();
  }

  private _setDisableOrEnable(): void {
    if (this.disabled !== undefined) {
      if (this.disabled) {
        this._disable();
      } else {
        this._enable();
      }
    }
  }
}
